import Constants from '@/Constants';
import { PushNotificationPaginationData } from '@/models/PushNotificationPaginationData';
import { PushNotificationRequest } from '@/models/PushNotificationRequest';
import { ApplicationState } from '@/store';
import { getPushNotificationListRequest } from '@/store/pushNotification/action';
import { PushNotification, PushNotificationTypes } from '@/store/pushNotification/types';
import PerfectScrollbar from 'perfect-scrollbar';
import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import EmptySection from '../EmptySection';
import Loader from '../Loader';
import NotificationCard from '../NotificationCard';
import './style.scss';

interface Props {
    active?: boolean;
}

const UncheckedNotifications: React.FC<Props> = (props: Props) => {
    const { active } = props;

    const { listOfUncheckedNotifications, listTotal, loading, strings } = useSelector((state: ApplicationState) => ({
        listOfUncheckedNotifications: state.pushNotification.data.listUncheckedPushNotification.list,
        listTotal: state.pushNotification.data.listUncheckedPushNotification.total,
        loading: state.pushNotification.loading,
        strings: state.locale.data,
    }));

    const dispatch = useDispatch();

    const [perfectScrollbar, setPerfectScrollbar] = useState<PerfectScrollbar>();
    const [currentListSize, setCurrentListSize] = useState<number>(0);

    useEffect(() => {
        setPerfectScrollbar(new PerfectScrollbar('#unchecked-scrollbar', { wheelPropagation: false }));
        if (listOfUncheckedNotifications.length === 0) {
            getNotificationsList();
        }
    }, [dispatch]);

    useEffect(() => {
        if (listOfUncheckedNotifications.length < currentListSize) {
            getNotificationsList(currentListSize - listOfUncheckedNotifications.length);
        }
        updatePerfectScrollbar();
        setCurrentListSize(listOfUncheckedNotifications.length);
    }, [listOfUncheckedNotifications.length]);

    useEffect(() => {
        if (active) {
            updatePerfectScrollbar();
        }
    }, [active]);

    const getNotificationsList = (limit: number = 5) => {
        const pushNotificationRequest = {
            uncheckedOnly: true,
            paginationData: {
                limit,
                offset: listOfUncheckedNotifications.length,
                requestSuccessType: PushNotificationTypes.UNCHECKED_PUSH_NOTIFICATION_LIST_SUCCESS,
            } as PushNotificationPaginationData,
        } as PushNotificationRequest;

        dispatch(getPushNotificationListRequest(pushNotificationRequest));
    };

    const updatePerfectScrollbar = () => {
        if (perfectScrollbar) {
            setTimeout(() => {
                perfectScrollbar.update();
            }, 500);
        }
    };

    const callback = useCallback(() => {
        if (listOfUncheckedNotifications.length < listTotal && !loading) {
            getNotificationsList();
        }
    }, [listOfUncheckedNotifications.length]);

    const scrollRef: RefObject<HTMLDivElement> = useBottomScrollListener(callback, { offset: 200 });

    return (
        <div id="unchecked-scrollbar" ref={scrollRef}>
            {listOfUncheckedNotifications.map((notification: PushNotification) => (
                <NotificationCard key={`unchecked_${notification.id}`} notification={notification} />
            ))}
            {!loading && listOfUncheckedNotifications.length === 0 && (
                <EmptySection iconName={Constants.icons.SMS_FAILED} message={strings.EMPTY_SECTION_MESSAGE} style={{ height: '100%' }} />
            )}
            {loading && listOfUncheckedNotifications.length === 0 && <Loader />}
            {loading && listOfUncheckedNotifications.length !== 0 && (
                <Loader spinnerHeight={30} spinnerWidth={30} styles={{ height: '12%', padding: 0 }} />
            )}
        </div>
    );
};

export default UncheckedNotifications;
